import { getSmartCollection } from 'api/product/getSmartCollection';
import IconArrowLeft from 'assets/icons/arrow-left.svg';
import cn from 'classnames';
import Container from 'components/container';
import FunnelCta from 'components/funnel/cta';
import ProductTile from 'components/legacy-product-tile';
import PawLoader from 'components/paw-loader';
import { useConfig } from 'config/use-config';
import { useCart } from 'contexts/cart';
import { PCMode, useProductConfigurator } from 'hooks/product/use-product-configurator';
import { isSubscriptionItem } from 'interfaces/line-item';
import { CollectionProduct } from 'models/collection/collection';
import { Collection, transformCollection } from 'models/collection/transformCollection';
import { Fragment, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSavingPlanStore } from 'stores/saving-plan';
import useSWR from 'swr';
import { CATEGORIES, FUNNEL_STEPS } from '../constants';
import { useSubscriptionFunnel } from '../state';
import { toLocaleCategory, toShopifyCategory } from '../utils';

const PRODUCTS_PER_BATCH = 3;

interface CollectionData {
  [key: string]: Collection;
}

interface ProductListItem {
  collectionHandle: string;
  data: CollectionProduct;
}

const StepProducts: React.FC = () => {
  const { shopId } = useConfig();
  const { open } = useProductConfigurator();
  const { goToStep, petType, selectedCategories, isExistingSubscription } = useSubscriptionFunnel();
  const { items } = useCart();
  const { formatMessage } = useIntl();
  const activePlan = useSavingPlanStore((state) => state.activePlan);
  const [selectedCategory, setSelectedCategory] = useState<CATEGORIES | null>(null);

  const handles = useMemo(() => {
    return selectedCategories.map(
      (category) => `subscriptionfunnel-${petType}-${toLocaleCategory(category)}`
    );
  }, [selectedCategories, petType]);

  // Create a stable SWR key by serializing the handles array
  const swrKey = useMemo(() => {
    if (handles.length === 0) return null;
    return `collections-${handles.sort().join(',')}-${shopId}`;
  }, [handles, shopId]);

  const fetcher = async (): Promise<CollectionData> => {
    const data: CollectionData = {};

    await Promise.all(
      handles.map(async (handle) => {
        const response = await getSmartCollection({ slug: handle }, activePlan);
        data[handle] = transformCollection({
          collection: response,
          shopId,
        });
      })
    );

    return data;
  };

  const { data: collectionData, isLoading } = useSWR(swrKey, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  /**
   * Generate product list based on available collections
   */
  const products = useMemo(() => {
    if (!collectionData) return [];

    const products: ProductListItem[] = [];
    const maxProducts = Math.max(
      ...handles.map((handle) => collectionData[handle]?.products.length || 0)
    );

    for (let i = 0; i < maxProducts; i += PRODUCTS_PER_BATCH) {
      handles.forEach((handle) => {
        if (collectionData[handle]) {
          const batch = collectionData[handle].products
            .slice(i, i + PRODUCTS_PER_BATCH)
            .map((product) => ({ collectionHandle: handle, data: product }));
          products.push(...batch);
        }
      });
    }

    return products;
  }, [handles, collectionData]);

  // Filter products by category according to user choice
  const filteredProducts = useMemo(() => {
    return selectedCategory
      ? products.filter(
          (product) => product.data.productType === toShopifyCategory(selectedCategory)
        )
      : products;
  }, [selectedCategory, products]);

  return (
    <div
      className="bg-other-white w-full lg:max-w-2xl lg:rounded-lg lg:shadow-lg"
      data-test="subscription-funnel-products"
    >
      <Container>
        <h1 className="text-primary-main flex text-xl leading-6 font-medium lg:pt-4 lg:text-4xl lg:leading-10 lg:font-black">
          <button
            className="focus-default mr-4 pt-1 pb-2"
            onClick={() => {
              goToStep(FUNNEL_STEPS.PREFERENCE);
            }}
            aria-label={formatMessage({
              id: 'subscription-funnel:products:back-button:label',
            })}
            data-test="subscription-funnel-back-button"
          >
            <IconArrowLeft className="h-6 w-6" />
          </button>
          <FormattedMessage id="subscription-funnel:products:header" />
        </h1>

        <p className="text-text-light-bg-secondary mt-2 lg:mt-4 lg:text-lg">
          <FormattedMessage id="subscription-funnel:products:sub-header" />
        </p>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-4 text-center">
            <PawLoader />
            <div className="text-primary-main pt-2 font-medium">
              <FormattedMessage id="subscription-funnel:products:info-loading" />
            </div>
          </div>
        ) : (
          <>
            <div
              className="text-grey-700 my-3 font-medium"
              data-test="subscription-funnel-results-count"
            >
              {filteredProducts.length}{' '}
              <FormattedMessage id="subscription-funnel:products:results" />
            </div>
            <div className="my-2 flex flex-wrap">
              {selectedCategories.length > 1 &&
                selectedCategories.map((category) => (
                  <button
                    key={category}
                    className={cn(
                      'text-primary-main my-2 mr-2 rounded-lg border p-2 shadow-sm',
                      category === selectedCategory
                        ? 'border-primary-main font-black'
                        : 'border-grey-50'
                    )}
                    onClick={() =>
                      category === selectedCategory
                        ? setSelectedCategory(null)
                        : setSelectedCategory(category)
                    }
                    aria-pressed={category === selectedCategory}
                    data-test={`subscription-funnel-category-${category}`}
                  >
                    <FormattedMessage id={`subscription-funnel:${category.toUpperCase()}`} />
                  </button>
                ))}
            </div>

            {selectedCategory && (
              <div
                className="text-primary-main text-lg font-black"
                data-test="subscription-funnel-selected-category"
              >
                <FormattedMessage id={`subscription-funnel:${selectedCategory.toUpperCase()}`} />
              </div>
            )}

            <div className="list flex flex-wrap md:-mx-2 md:px-0" data-test="collection-list">
              {filteredProducts.map((product, index) => {
                const isSelected = items.some((item) => {
                  return product.data.id === String(item.productId) && isSubscriptionItem(item);
                });

                if (!product.data.availableForSale) {
                  return <Fragment key={index} />;
                }

                return (
                  <div className="mb-3 flex w-full md:mb-0 md:w-1/2 md:p-2" key={index}>
                    <ProductTile
                      selected={isSelected}
                      product={product.data}
                      collectionTitle={collectionData?.[product.collectionHandle]?.title || ''}
                      position={index}
                      showSubscriptionPrice
                      onTileClick={() => {
                        open(product.data, {
                          mode: PCMode.FUNNEL,
                          hasExistingSubscription: isExistingSubscription,
                        });
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}

        <FunnelCta
          visible={selectedCategories.length > 0 && petType !== null}
          onClick={() => {
            goToStep(FUNNEL_STEPS.OVERVIEW);
          }}
        >
          <FormattedMessage id="subscription-funnel:button:continue" />
        </FunnelCta>
      </Container>
    </div>
  );
};

export default StepProducts;
